/*
 * ConsoleCommands — Dispatcher fuer Slash-Kommandos der Core Console.
 *
 * Wird von CoreConsole._handleSend(text) aufgerufen, bevor der Text an den
 * AgentService geht. Gibt { handled: true } zurueck, wenn das Kommando
 * erkannt und ausgefuehrt wurde — die Console sendet den Text dann nicht
 * weiter an den AgentService.
 *
 * Aktuell nur /mock (Delta-Review-Scenarios, Phase 0.5).
 *
 * Dev-Guard: /mock ist nur auf localhost / 127.0.0.1 aktiv. In Produktion
 * wird das Kommando nicht erkannt — der Text laeuft dann stumpf als
 * Chat-Nachricht durch (was in Produktion ohnehin nichts macht, weil die
 * Scenario-Dateien aus dem Deploy rausfallen; der Dev-Guard ist
 * Defense-in-Depth).
 */

import { MockAgentTransport } from '../agent/MockAgentTransport.js'

/**
 * Prueft, ob /mock im aktuellen Browser-Kontext verfuegbar ist.
 * @returns {boolean}
 */
export function isMockEnabled() {
    if (typeof window === 'undefined') return false
    const host = window.location?.hostname || ''
    return host === 'localhost' || host === '127.0.0.1' || host === ''
}

/**
 * Dispatcher fuer Console-Slash-Kommandos.
 *
 * Benutzt von CoreConsole:
 *   const disp = new ConsoleCommandDispatcher(coreConsole, mockTransport)
 *   const handled = disp.handle('/mock list')   // true/false
 */
export class ConsoleCommandDispatcher {
    /**
     * @param {CoreConsole} coreConsole - fuer log() und _mockActiveScenario-Flag
     * @param {MockAgentTransport} mockTransport - Phase 0.5 Transport
     */
    constructor(coreConsole, mockTransport) {
        this.console = coreConsole
        this.mockTransport = mockTransport
    }

    /**
     * Erkennt und verarbeitet Slash-Kommandos.
     * @param {string} text - Roher Console-Input
     * @returns {{handled: boolean, consumeInput?: boolean}}
     *   handled=true      → Dispatcher hat das Kommando bearbeitet
     *   consumeInput=true → der Text soll NICHT als User-Entry gespeichert
     *                       und auch nicht an den AgentService weitergereicht
     *                       werden. Default bei handled=true.
     */
    handle(text) {
        const trimmed = text.trim()
        if (!trimmed.startsWith('/')) return { handled: false }

        // Split auf Whitespace (mehrfache Spaces okay)
        const parts = trimmed.split(/\s+/)
        const command = parts[0].toLowerCase()
        const args = parts.slice(1)

        switch (command) {
            case '/mock':
                return this._handleMock(args)
            default:
                // Unbekanntes Slash-Kommando: NICHT als Chat weitergeben,
                // sondern freundlich ablehnen. Slash-Praefix ist ein klares
                // Signal, dass der User ein Kommando wollte.
                this.console.log(`Unknown command: ${command}`, 'ERROR')
                return { handled: true, consumeInput: true }
        }
    }

    /**
     * /mock list              -> verfuegbare Scenarios listen
     * /mock <name>            -> Scenario laden + Session ausloesen
     * /mock repeat            -> letztes Scenario erneut starten
     * /mock off               -> Mock deaktivieren
     * /mock                   -> Hilfe anzeigen
     */
    _handleMock(args) {
        if (!isMockEnabled()) {
            this.console.log('Mock scenarios are only available in development.', 'ERROR')
            return { handled: true, consumeInput: true }
        }

        if (!this.mockTransport) {
            this.console.log('Mock transport not available.', 'ERROR')
            return { handled: true, consumeInput: true }
        }

        const subcommand = (args[0] || '').toLowerCase()

        if (!subcommand) {
            this.console.log('Usage: /mock list | /mock <name> | /mock repeat | /mock off')
            return { handled: true, consumeInput: true }
        }

        if (subcommand === 'list') {
            const list = MockAgentTransport.listScenarios()
            if (list.length === 0) {
                this.console.log('No scenarios registered.')
            } else {
                this.console.log('Available scenarios:')
                for (const s of list) {
                    this.console.log(`  ${s.name} — ${s.description}`)
                }
            }
            return { handled: true, consumeInput: true }
        }

        if (subcommand === 'off') {
            this.mockTransport.clearScenario()
            // Auch die Markierung im Console loeschen (Phase A: per Flag am
            // coreConsole gehalten — siehe CoreConsole._mockScenarioPending)
            if (this.console._clearMockScenarioPending) {
                this.console._clearMockScenarioPending()
            }
            this.console.log('Mock disabled.')
            return { handled: true, consumeInput: true }
        }

        if (subcommand === 'repeat') {
            const last = this.mockTransport.getLastScenarioName()
            if (!last) {
                this.console.log('No previous mock scenario to repeat.', 'ERROR')
                return { handled: true, consumeInput: true }
            }
            return this._startScenario(last)
        }

        // Default: Scenario-Name
        return this._startScenario(subcommand)
    }

    _startScenario(scenarioName) {
        const loaded = this.mockTransport.loadScenario(scenarioName)
        if (!loaded) {
            this.console.log(`Unknown scenario: ${scenarioName}. Try /mock list.`, 'ERROR')
            return { handled: true, consumeInput: true }
        }
        this.console.log(`Mock scenario "${scenarioName}" armed. Press Enter to start.`)
        if (this.console._setMockScenarioPending) {
            this.console._setMockScenarioPending(scenarioName)
        }
        return { handled: true, consumeInput: true }
    }
}
